import React from "react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, Zap, Package } from "lucide-react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import OptimizedImage from "@/components/OptimizedImage";
import HomeSupportSection from "@/components/HomeSupportSection";
import {
  generateOrganizationSchema,
  generateLocalBusinessSchema,
  generateWebsiteSchema,
} from "@/lib/schemas";

export default function Home() {
  const publishedPagesRef = useRef<HTMLElement>(null);
  const shouldLoadPublishedPages = useNearViewport(publishedPagesRef);
  const { data: publishedPages = [] } = trpc.products.getPublishedPages.useQuery(undefined, {
    enabled: shouldLoadPublishedPages,
  });
  useEffect(() => {
    const schemas = [
      generateOrganizationSchema(),
      generateLocalBusinessSchema(),
      generateWebsiteSchema(),
    ];
    const scripts = schemas.map((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach((script) => script.remove());
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <section className="px-4 py-16 md:py-28 max-w-6xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <Badge className="mb-6 bg-blue-50 text-blue-700 hover:bg-blue-100 px-4 py-2">
            Australian IGET Information & Product Guide
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Explore the IGET range
            <br />
            <span className="text-blue-600">with clearer answers</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-10 leading-8">
            Discover IGET product information, flavour guidance, usage support, and Australian
            service resources in one focused destination. Compare options within the IGET range
            and choose the information that matches your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/products/all">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                All IGET products <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a
              href="https://igetalibar.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                Browse sales entry <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <Link href="/products/iget">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                Read IGET guide <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link href="/find-retailers">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-blue-200 text-blue-700 hover:bg-blue-50">
                Find offline retailers <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <HomeSupportSection />

      <section className="bg-gray-50 px-4 py-14 md:py-20" data-track-block="iget-overview">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">
              One brand, one knowledge base
            </p>
            <h2 className="text-3xl font-bold text-gray-900 md:text-5xl">
              Everything you need to understand IGET
            </h2>
            <p className="mt-4 text-base leading-7 text-gray-600 md:text-lg">
              Start with the format, flavour, and support information that matches your question—without leaving this IGET information hub.
            </p>
          </div>
          <div className="grid grid-cols-3 items-stretch gap-6 max-md:grid-cols-1">
            <Card className="h-full border-0 p-6 shadow-lg transition-shadow hover:shadow-xl" data-track-block="iget-product-card">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Explore the range</p>
              <div className="mb-6 flex h-44 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-slate-100">
                <OptimizedImage
                  src="/manus-storage/iget-product-card-640_98cd1e95.webp"
                  srcSet="/manus-storage/iget-product-card-320_037a5272.webp 320w, /manus-storage/iget-product-card-480_ac78abff.webp 480w, /manus-storage/iget-product-card-640_98cd1e95.webp 640w"
                  alt="IGET ONE product information"
                  width={640}
                  height={640}
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 767px) calc(100vw - 3rem), (max-width: 1200px) 30vw, 360px"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <h3 className="mb-3 text-2xl font-bold text-gray-900">IGET product range</h3>
              <p className="mb-5 text-gray-600">
                Compare IGET formats, read copied product details, and find the key specifications before choosing an information path.
              </p>
              <div className="space-y-2 border-t border-slate-100 pt-4">
                <Link href="/products/all" className="flex items-center justify-between text-sm font-semibold text-blue-700 hover:text-blue-900">
                  <span>Browse all IGET products</span><ArrowRight className="h-4 w-4" />
                </Link>
                <Link href="/products/iget" className="flex items-center justify-between text-sm font-semibold text-slate-700 hover:text-blue-700">
                  <span>Read the IGET guide</span><ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Card>
            <Card className="h-full border-0 p-6 shadow-lg transition-shadow hover:shadow-xl">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Find your fit</p>
              <div className="mb-6 overflow-hidden rounded-xl bg-blue-50/40 ring-1 ring-blue-100">
                <OptimizedImage
                  src="/manus-storage/iget-flavour-card-640_108a25f4.webp"
                  srcSet="/manus-storage/iget-flavour-card-320_fb444bfa.webp 320w, /manus-storage/iget-flavour-card-480_3f74ae4a.webp 480w, /manus-storage/iget-flavour-card-640_108a25f4.webp 640w"
                  alt="IGET flavour and format discovery information"
                  width={640}
                  height={360}
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 767px) calc(100vw - 3rem), (max-width: 1200px) 30vw, 360px"
                  className="h-44 w-full object-cover"
                />
              </div>
              <h3 className="mb-3 text-2xl font-bold text-gray-900">Flavour discovery</h3>
              <p className="mb-5 text-gray-600">
                Organise flavour and format information with the IGET-focused quiz, then review product pages for the details behind each result.
              </p>
              <div className="space-y-2 border-t border-slate-100 pt-4">
                <Link href="/recommendation" className="flex items-center justify-between text-sm font-semibold text-blue-700 hover:text-blue-900">
                  <span>Start the IGET quiz</span><ArrowRight className="h-4 w-4" />
                </Link>
                <Link href="/products/all" className="flex items-center justify-between text-sm font-semibold text-slate-700 hover:text-blue-700">
                  <span>Compare product formats</span><ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Card>
            <Card className="h-full border-0 p-6 shadow-lg transition-shadow hover:shadow-xl">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-green-700">Support & guidance</p>
              <div className="mb-6 overflow-hidden rounded-xl bg-green-50/40 ring-1 ring-green-100">
                <OptimizedImage
                  src="/manus-storage/iget-support-card-640_96d4a291.webp"
                  srcSet="/manus-storage/iget-support-card-320_f67e3dec.webp 320w, /manus-storage/iget-support-card-480_3c8b5840.webp 480w, /manus-storage/iget-support-card-640_96d4a291.webp 640w"
                  alt="IGET responsible support and guidance information"
                  width={640}
                  height={360}
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 767px) calc(100vw - 3rem), (max-width: 1200px) 30vw, 360px"
                  className="h-44 w-full object-cover"
                />
              </div>
              <h3 className="mb-3 text-2xl font-bold text-gray-900">Responsible support</h3>
              <p className="mb-5 text-gray-600">
                Find age, service, delivery, and usage guidance through the FAQ and buying guide, with clear routes to human assistance.
              </p>
              <div className="space-y-2 border-t border-slate-100 pt-4">
                <Link href="/faq" className="flex items-center justify-between text-sm font-semibold text-green-700 hover:text-green-900">
                  <span>Read support FAQs</span><ArrowRight className="h-4 w-4" />
                </Link>
                <Link href="/buying-guide" className="flex items-center justify-between text-sm font-semibold text-slate-700 hover:text-green-700">
                  <span>Open the buying guide</span><ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {publishedPages.length > 0 && (
        <section ref={publishedPagesRef} className="mx-auto max-w-6xl px-4 py-14 md:py-20" data-track-block="published-iget-products">
          <div className="mb-10 text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">New IGET product pages</p>
            <h2 className="text-3xl font-bold text-gray-900 md:text-5xl">Explore newly added products</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {publishedPages.slice(0, 3).map((product) => (
              <Card key={product.id} className="overflow-hidden border-0 shadow-lg">
                <div className="flex h-48 items-center justify-center bg-gray-50 p-4">
                  {(product.thumbnailUrl || product.imageUrls[0]) && (
                    <OptimizedImage src={product.thumbnailUrl || product.imageUrls[0]} alt={product.name} width={640} height={480} loading="lazy" decoding="async" sizes="(max-width: 767px) 100vw, 33vw" className="h-full w-full object-contain" />
                  )}
                </div>
                <div className="p-6">
                  <Badge className="bg-blue-50 text-blue-700">IGET</Badge>
                  <h3 className="mt-3 text-xl font-bold text-gray-900">{product.name}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-gray-600">{product.description || "Read the copied IGET product information and official source details."}</p>
                  <Link href={`/product-pages/${product.slug}`}>
                    <Button className="mt-5 w-full bg-blue-600 hover:bg-blue-700">View product page <ArrowRight className="ml-2 h-4 w-4" /></Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link href="/products/all">
              <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">View more products <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
        </section>
      )}

      <section className="px-4 py-14 md:py-20 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div>
            <Package className="w-8 h-8 text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Australian service information</h3>
            <p className="text-gray-600">Find delivery and support guidance in one place.</p>
          </div>
          <div>
            <Check className="w-8 h-8 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Clear product details</h3>
            <p className="text-gray-600">Read structured IGET product and flavour information.</p>
          </div>
          <div>
            <Zap className="w-8 h-8 text-purple-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Helpful guides</h3>
            <p className="text-gray-600">Use FAQs, buying guidance, and the IGET quiz.</p>
          </div>
        </div>
      </section>

      <section className="px-4 py-14 md:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
            Latest IGET guides and updates
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Read the latest product education, service guidance, and Australian market updates.
          </p>
          <NewsArticlesSection />
        </div>
      </section>

      <section className="px-4 py-16 md:py-24 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Find your IGET information path</h2>
          <p className="text-lg mb-8 opacity-90">
            Explore the IGET guide, read the FAQ, or use the focused recommendation quiz.
          </p>
          <Link href="/recommendation">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              Start IGET quiz <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
        <div>Copyright © 2024 IGETALIBAR All rights reserved.</div>
        <nav className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1" aria-label="Legal links">
          <Link href="/privacy-policy" className="text-blue-700 underline-offset-4 hover:underline">
            Privacy Policy
          </Link>
          <span aria-hidden="true" className="text-slate-300">|</span>
          <Link href="/terms-of-use" className="text-blue-700 underline-offset-4 hover:underline">
            Terms of Use
          </Link>
        </nav>
      </footer>
    </div>
  );
}

function NewsArticlesSection() {
  const [currentPage, setCurrentPage] = useState(1);
  const articlesRef = useRef<HTMLDivElement>(null);
  const shouldLoadArticles = useNearViewport(articlesRef);
  const pageSize = 6;
  const { data: articlesData, isLoading } = trpc.articles.getPublished.useQuery({
    page: currentPage,
    pageSize,
  }, {
    enabled: shouldLoadArticles,
  });
  const articles = articlesData?.articles || [];
  const totalPages = articlesData ? Math.ceil(articlesData.total / pageSize) : 1;

  if (!shouldLoadArticles) {
    return <div ref={articlesRef} className="min-h-80" role="status" aria-live="polite" aria-busy="true" aria-label="Loading IGET guides" />;
  }
  if (isLoading) {
    return <div ref={articlesRef} className="grid md:grid-cols-3 gap-8" role="status" aria-live="polite" aria-busy="true" aria-label="Loading IGET guides">{[...Array(6)].map((_, i) => <div key={i} className="bg-gray-200 rounded-lg h-80 animate-pulse" aria-hidden="true" />)}</div>;
  }
  if (articles.length === 0) {
    return <div className="text-center py-12"><p className="text-gray-600 text-lg">No IGET articles published yet. Check back soon!</p></div>;
  }

  return (
    <div ref={articlesRef}>
      <div className="grid gap-4 md:gap-8 mb-8 md:mb-12">
        {articles.map((article: any) => {
          const title = article.englishTitle || article.title;
          const summary = article.englishSummary || article.englishContent?.substring(0, 150) || article.summary || article.content?.substring(0, 150) || "";
          return (
            <Card key={article.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              {article.featuredImage && <div className="h-32 md:h-48 overflow-hidden bg-gray-100"><OptimizedImage src={article.featuredImage} alt={title} width={640} height={360} loading="lazy" decoding="async" sizes="(max-width: 767px) 100vw, 33vw" className="w-full h-full object-cover" /></div>}
              <div className="p-4 md:p-6">
                {article.category && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs mb-3">{article.category}</Badge>}
                <h3 className="text-base md:text-xl font-bold text-gray-900 mb-3 line-clamp-2">{title}</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3">{summary.substring(0, 150)}...</p>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4"><span>{article.author}</span><span>{new Date(article.publishedAt).toLocaleDateString()}</span></div>
                <Link href={`/articles/${article.id}`}><Button variant="outline" className="w-full">Read More <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              </div>
            </Card>
          );
        })}
      </div>
      {totalPages > 1 && <div className="flex justify-center items-center gap-4"><Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Previous</Button><span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span><Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</Button></div>}
    </div>
  );
}


function useNearViewport<T extends Element>(targetRef: React.RefObject<T | null>) {
  const [isNearViewport, setIsNearViewport] = useState(false);

  useEffect(() => {
    const target = targetRef.current;
    if (!target || isNearViewport) return;

    if (!("IntersectionObserver" in window)) {
      setIsNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "500px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [isNearViewport, targetRef]);

  return isNearViewport;
}
